import { auth } from '../../firebase/firebase';

// Server URL from environment variable (required)
export const SERVER_URL = import.meta.env.VITE_SERVER_URL;

if (!SERVER_URL) {
  throw new Error('VITE_SERVER_URL environment variable is not set');
}

// Simple token cache to avoid redundant token requests
let tokenCache = {
  token: null,
  timestamp: 0,
  uid: null
};

// Token cache TTL - 50 minnutes (tokens expire after 1 hour)
const TOKEN_CACHE_TTL = 50 * 60 * 1000;

// Function to clear the token cache (useful when user changes)
export function clearTokenCache() {
  tokenCache = { token: null, timestamp: 0, uid: null };
}

// Custom error class for server errors
export class ServerError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ServerError';
    this.status = status;
  }
}

/**
 * Get the current Firebase user
 */
const getCurrentUser = () => auth.currentUser;

/**
 * Makes an authenticated API request to the server
 * @param {string} endpoint - The API endpoint (without the base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export async function apiRequest(endpoint, options = {}) {
  try {
    const currentUser = getCurrentUser();
    let idToken = null;
    
    // Get authentication token with caching
    if (currentUser) {
      const currentUid = currentUser.uid;
      const now = Date.now();
      
      // Use cached token if valid
      if (tokenCache.token && 
          tokenCache.uid === currentUid && 
          (now - tokenCache.timestamp) < TOKEN_CACHE_TTL) {
        idToken = tokenCache.token;
      } else {
        // Get fresh token
        try {
          idToken = await currentUser.getIdToken(false);
          tokenCache = { token: idToken, timestamp: now, uid: currentUid };
        } catch (tokenError) {
          // Force refresh on error
          idToken = await currentUser.getIdToken(true);
          tokenCache = { token: idToken, timestamp: now, uid: currentUid };
        }
      }
    }
    
    // Prepare headers
    const headers = {
      ...(idToken && { 'Authorization': `Bearer ${idToken}` }),
      ...options.headers
    };
    
    // Set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Setup timeout
    const timeout = options.timeout || 10000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    const { timeout: _timeout, ...fetchOptions } = options;
    
    // Make the request
    const response = await fetch(`${SERVER_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Handle 401 - retry with fresh token
    if (response.status === 401 && currentUser) {
      tokenCache = { token: null, timestamp: 0, uid: null };
      const freshToken = await currentUser.getIdToken(true);
      tokenCache = { token: freshToken, timestamp: Date.now(), uid: currentUser.uid };
      
      const retryResponse = await fetch(`${SERVER_URL}${endpoint}`, {
        ...fetchOptions,
        headers: { ...headers, 'Authorization': `Bearer ${freshToken}` }
      });
      
      if (!retryResponse.ok && retryResponse.status >= 500) {
        throw new ServerError(`Server error: ${retryResponse.status}`, retryResponse.status);
      }
      return retryResponse;
    }
    
    // Handle server errors
    if (!response.ok && response.status >= 500) {
      throw new ServerError(`Server error: ${response.status}`, response.status);
    }
    
    return response;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ServerError('Request timed out', 408);
    }
    if (error instanceof ServerError) {
      throw error;
    }
    throw new ServerError(`Network error: ${error.message}`, 0);
  }
}

// Helper function to get cached data
export function getCachedData(key) {
  const memoryCache = window.__API_CACHE = window.__API_CACHE || {};
  return memoryCache[key];
}

// Helper function to set cached data
export function setCachedData(key, data) {
  const memoryCache = window.__API_CACHE = window.__API_CACHE || {};
  memoryCache[key] = {
    data,
    timestamp: Date.now()
  };
}

/**
 * Makes a GET request to the API
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Additional fetch options
 * @param {boolean} options.useCache - Whether to use cached response
 * @param {boolean} options.skipFetch - If true, only return from cache without making a network request
 * @param {boolean} options.force - Force a fresh fetch even if cached data exists
 * @returns {Promise<any>} - The parsed JSON response
 */
export async function get(endpoint, options = {}) {
  const { useCache, skipFetch, force, ...fetchOptions } = options;
  const cacheKey = `api_cache_${endpoint}`;
  const cacheTTL = 60 * 1000; // 1 minute
  
  // Check cache
  if (useCache) {
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData && !force) {
      if (skipFetch) return cachedData.data;
      if (Date.now() - cachedData.timestamp < cacheTTL) return cachedData.data;
    }
    
    if (skipFetch) return null;
  }
  
  try {
    const response = await apiRequest(endpoint, { method: 'GET', ...fetchOptions });
    
    if (!response.ok) {
      if (response.status === 404) return null;
      
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `API GET error: ${response.status} ${response.statusText}`;
      } catch {
        errorMessage = `API GET error: ${response.status} ${response.statusText}`;
      }
      
      if (response.status >= 500) {
        throw new ServerError(`Server error: ${response.status} ${response.statusText}`, response.status);
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    if (useCache) setCachedData(cacheKey, data);
    
    return data;
  } catch (error) {
    if (error instanceof ServerError) throw error;
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new ServerError('Invalid response from server', 500);
    }
    throw error;
  }
}

/**
 * Makes a POST request to the API
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - The parsed JSON response
 */
export async function post(endpoint, data, options = {}) {
  try {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const response = await apiRequest(endpoint, { method: 'POST', body, ...options });
    
    if (!response.ok) {
      let errorMessage, errorData = null;
      
      try {
        errorData = await response.json();
        errorMessage = errorData.error || `API POST error: ${response.status} ${response.statusText}`;
      } catch {
        errorMessage = `API POST error: ${response.status} ${response.statusText}`;
      }
      
      if (response.status >= 500) {
        const serverError = new ServerError(
          endpoint === '/auth/signup' 
            ? 'Server error occurred during signup. Please try again later.'
            : `Server error: ${response.status} ${response.statusText}`,
          response.status
        );
        serverError.response = { status: response.status, data: errorData };
        throw serverError;
      }
      
      const error = new Error(errorMessage);
      error.response = { status: response.status, data: errorData };
      throw error;
    }
    
    // Handle empty responses
    if (response.headers.get('content-length') === '0' || response.status === 204) {
      return null;
    }
    
    try {
      return await response.json();
    } catch {
      return { success: true };
    }
  } catch (error) {
    if (error instanceof ServerError) throw error;
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new ServerError('Invalid response from server', 500);
    }
    if (error.name === 'AbortError') {
      const abortError = new ServerError('Request timed out. The server took too long to respond.', 408);
      abortError.isTimeout = true;
      throw abortError;
    }
    if (error.message?.includes('Failed to fetch') || error.message?.includes('Network request failed')) {
      const networkError = new ServerError('Network error. Please check your internet connection.', 0);
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
}

/**
 * Makes a PUT request to the API
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - The parsed JSON response
 */
export async function put(endpoint, data, options = {}) {
  try {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const response = await apiRequest(endpoint, { method: 'PUT', body, ...options });
    
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `API PUT error: ${response.status} ${response.statusText}`;
      } catch {
        errorMessage = `API PUT error: ${response.status} ${response.statusText}`;
      }
      
      if (response.status >= 500) {
        throw new ServerError(`Server error: ${response.status} ${response.statusText}`, response.status);
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  } catch (error) {
    if (error instanceof ServerError) throw error;
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new ServerError('Invalid response from server', 500);
    }
    throw error;
  }
}

/**
 * Makes a DELETE request to the API
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - The parsed JSON response
 */
export async function del(endpoint, options = {}) {
  try {
    const response = await apiRequest(endpoint, { method: 'DELETE', ...options });
    
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `API DELETE error: ${response.status} ${response.statusText}`;
      } catch {
        errorMessage = `API DELETE error: ${response.status} ${response.statusText}`;
      }
      
      if (response.status >= 500) {
        throw new ServerError(`Server error: ${response.status} ${response.statusText}`, response.status);
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  } catch (error) {
    if (error instanceof ServerError) throw error;
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new ServerError('Invalid response from server', 500);
    }
    throw error;
  }
}

// Export everything as a default object as well
export default {
  SERVER_URL,
  ServerError,
  apiRequest,
  get,
  post,
  put,
  del,
  getCachedData,
  setCachedData,
  clearTokenCache
}; 